import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import TextButton from 'LuxKit/Button/TextButton'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { connect } from 'react-redux'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { rem } from 'polished'
import { memo, useCallback, useContext, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import TourV2DepartureSoldOutTile from './TourV2DepartureSoldOutTile'
import { checkPurchasableOptionHasPackageUpgradeOrRoomType, getTourPricePerDay, getTourV2VariationDuration, getTourValueAtPerDay } from 'lib/tours/tourUtils'
import { sum } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import FormatCurrency from 'components/Common/FormatCurrency'
import TourV2DeparturePricingDropdown from './TourV2DeparturePricingDropdown'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import { isCruiseFlashOfferV2 } from 'lib/offer/offerTypes'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import TourV2DepartureTileDates from './TourV2DepartureTileDates'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TourDepositTag from '../TourDepositTag'
import moment from 'moment'
import ModalContext from 'contexts/ModalContext'
import type { TourDepositInfoModalResult } from '../TourDepositInfoModal'
import TourDepositInfoModal from '../TourDepositInfoModal'
import PriceRowSale from 'LuxKit/PricePoints/PriceRowSale'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import noop from 'lib/function/noop'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorTourOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import { maybeWithValue } from 'lib/offer/pricing'
import { getTourPriceMode } from 'selectors/TourV2/toursSelectors'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import Label from 'LuxKit/Label/Label'
import { TourOptionPackageUpgrades } from 'constants/tours'
import TextLink from 'LuxKit/TextLink'
import PrivateRequestFormModal from '../PrivateRequest/PrivateRequestFormModal'
import { isPrivateRequestEnabled } from 'selectors/TourV2/tourV2OfferPageSelectors'

const PackageLabel = styled(Label)``

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "tags"
  "member"
  "prices";

  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr ${rem(190)} min-content;
    grid-template-areas:
    "dates prices cta"
    "tags prices cta"
    "member prices cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(320)} 1fr ${rem(190)} min-content;
    grid-template-areas:
      "dates tags prices cta"
      "dates member prices cta";
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: ${rem(320)} ${rem(180)} ${rem(210)} ${rem(190)} min-content;
    grid-template-areas: "dates tags member prices cta";
  }
`

const TagGridArea = styled(Group)`
  grid-area: tags;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: end;
  }
`

const MemberGridArea = styled(Group)`
  grid-area: member;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: start;
  }
`

interface Props {
  offer: App.Tours.TourV2Offer
  departure: App.Tours.TourV2OfferDeparture
  travellers: Array<OfferPageRoom>
  variation: App.Tours.TourV2OfferVariation
  onSelect?: (departure: App.Tours.TourV2OfferDeparture, keepBrowsing?: boolean) => void
  packageType?: string
}
interface MappedStateProps {
  isSpoofing: boolean
}

function TourV2DepartureTile(props: Props & MappedStateProps) {
  const {
    offer,
    departure,
    onSelect = noop,
    travellers,
    variation,
    packageType,
  } = props

  const showModal = useContext(ModalContext)
  const intl = useIntl()

  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const tourPriceMode = useAppSelector(getTourPriceMode)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)

  const showPrivateRequest = isPrivateRequestEnabled(variation, currentRegionCode)

  const onRequestPrivateDeparture = useCallback(() => {
    showModal(<PrivateRequestFormModal offer={offer} selectedDeparture={departure} defaultTravellers={travellers} />)
  }, [showModal, offer, departure, travellers])

  const isCruiseFlashOffer = isCruiseFlashOfferV2(offer)

  const getPackageFromPrice = useCallback((candidatePackageType: string) => {
    const candidateViews = getDepartureOptionsForTravellers(departure, travellers, candidatePackageType)
    const candidateOptions = candidateViews.flatMap(view => view.options)
    if (candidateOptions.length === 0) return undefined

    // Keep the from-price calculation consistent with checkout (`TourPackageCheckoutPage`):
    // pick the lowest *effective* from price (LuxPlus member price when available).
    return candidateOptions.reduce<number | undefined>((lowest, purchasableOption) => {
      const effectiveFromPrice =
        isLuxPlusMember && purchasableOption.memberPrice
          ? purchasableOption.memberPrice
          : purchasableOption.price
      if (lowest === undefined || effectiveFromPrice < lowest) return effectiveFromPrice
      return lowest
    }, undefined)
  }, [departure, travellers, isLuxPlusMember])

  const availablePackageTypes = useMemo(() => {
    return TourOptionPackageUpgrades.filter((candidatePackageType) => {
      return getDepartureOptionsForTravellers(departure, travellers, candidatePackageType).length > 0
    })
  }, [departure, travellers])

  const selectedFromPrice = useMemo(() => {
    if (!packageType) return undefined
    return getPackageFromPrice(packageType)
  }, [getPackageFromPrice, packageType])

  const checkoutPackageTypes = useMemo(() => {
    // When a package is selected from the offer page, we only want to consider the selected
    // package and packages that are more expensive (by "From" price) to avoid showing cheaper downgrades.
    if (selectedFromPrice === undefined) return availablePackageTypes

    return availablePackageTypes.filter((candidatePackageType) => {
      if (candidatePackageType === packageType) return true
      const candidateFromPrice = getPackageFromPrice(candidatePackageType)
      if (candidateFromPrice === undefined) return false
      return candidateFromPrice >= selectedFromPrice
    })
  }, [availablePackageTypes, getPackageFromPrice, packageType, selectedFromPrice])

  const cheapestPackageTypeForTile = useMemo<string | undefined>(() => {
    // If the selected preferred package has inventory for this departure, use it.
    if (packageType && selectedFromPrice !== undefined) return packageType

    // Otherwise fall back to the cheapest available package (with inventory) for this departure.
    return checkoutPackageTypes.reduce<string | undefined>((cheapest, candidate) => {
      const candidateFromPrice = getPackageFromPrice(candidate)
      if (candidateFromPrice === undefined) return cheapest

      if (!cheapest) return candidate

      const cheapestFromPrice = getPackageFromPrice(cheapest)
      if (cheapestFromPrice === undefined || candidateFromPrice < cheapestFromPrice) return candidate
      return cheapest
    }, undefined)
  }, [checkoutPackageTypes, getPackageFromPrice, packageType, selectedFromPrice])

  const isCheapestPackageTypeSelected = useMemo(() => {
    // Only highlight if the label matches the selected package AND that selected package
    // is available for this departure.
    return !!packageType && cheapestPackageTypeForTile === packageType && selectedFromPrice !== undefined
  }, [cheapestPackageTypeForTile, packageType, selectedFromPrice])

  const views = useMemo(
    () => cheapestPackageTypeForTile
      ? getDepartureOptionsForTravellers(departure, travellers, cheapestPackageTypeForTile)
      : getDepartureOptionsForTravellers(departure, travellers),
    [travellers, departure, cheapestPackageTypeForTile],
  )
  const breakdowns = useMemo(() => views.map(view => {
    return generateDepartureRoomPriceBreakdown(view.room, view.cheapestOption, offer)
  }), [offer, views])

  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const total = useMemo(() => sum(breakdowns, breakdown => {
    if (canViewLuxPlusBenefits) {
      return breakdown.totalMemberPrice || breakdown.totalPrice
    }
    return breakdown.totalPrice
  }), [breakdowns, canViewLuxPlusBenefits])

  const cheapestOption = useMemo(() => {
    // Ensure the displayed "From" price aligns with the package represented by the label:
    // choose the lowest effective per-person price across all viable options (member price
    // when available for LuxPlus members, otherwise regular price).
    const allOptions = views.flatMap(view => view.options)
    if (allOptions.length === 0) return undefined

    return allOptions.reduce<App.Tours.TourV2OfferPurchasableOption | undefined>((cheapest, option) => {
      const optionEffectiveFromPrice =
        isLuxPlusMember && option.memberPrice
          ? option.memberPrice
          : option.price
      const cheapestEffectiveFromPrice =
        cheapest
          ? (isLuxPlusMember && cheapest.memberPrice ? cheapest.memberPrice : cheapest.price)
          : undefined

      if (!cheapest || cheapestEffectiveFromPrice === undefined || optionEffectiveFromPrice < cheapestEffectiveFromPrice) {
        return option
      }
      return cheapest
    }, undefined)
  }, [isLuxPlusMember, views])
  const onSelectClick = useCallback(() => {
    onSelect(departure)
  }, [departure, onSelect])

  const details = useMemo(() => cheapestOption ? extractTourV2PurchasableOptionDetails(cheapestOption) : undefined, [cheapestOption])
  const duration = useMemo<number | undefined>(() => {
    if (variation) {
      return getTourV2VariationDuration(variation)
    }
  }, [variation])
  const pricePerDayDetails = useMemo(() => {
    if (!details || tourPriceMode === 'total') return
    const pricePerDay = getTourPricePerDay(details.pricePerPerson, details.memberPricePerPerson, duration)
    const valueAt = getTourValueAtPerDay(details.valuedAt?.price, duration)

    if (pricePerDay) {
      return {
        pricing: maybeWithValue({
          price: pricePerDay.pricePerPersonPerDay,
          fees: [],
          saleUnit: 'day per person',
        }, details.valuedAt?.price),
        memberPricing: maybeWithValue({
          price: pricePerDay.memberPricePerPersonPerDay,
          fees: [],
          saleUnit: 'day per person',
        }, details.valuedAt?.price),
        valueAt,
      }
    }
  }, [details, duration, tourPriceMode])

  const { hasPackageUpgrade, hasRoomUpgrade } = useMemo(() => {
    return checkPurchasableOptionHasPackageUpgradeOrRoomType(views.flatMap(view => view.options))
  }, [views])

  const getPackageTagLabel = useCallback((candidatePackageType: string) => {
    const upgrade = variation.availablePackageUpgrades?.find((u) => u.packageType === candidatePackageType)
    if (upgrade?.packageOptionDisplayName) {
      return upgrade.packageOptionDisplayName
    }

    // Get offer-specific package text map key
    const offerIdWithoutPrefix = offer.id?.replace('tour-', '')
    const lowerCasedPackageType = candidatePackageType.toLowerCase()

    // Map specific offer IDs to their package text variants
    if (['bbf97caa-d857-4517-b768-465f5ceac8db', '15275af7-48cb-4a5e-ad0c-eb9b3efc1619', '6f77e732-d5e1-4747-ae47-901022e36fc3'].includes(offerIdWithoutPrefix)) {
      // Eco/Business package text
      if (lowerCasedPackageType === 'regular') {
        return intl.formatMessage({
          id: 'sYjFQEJVAkr_ad4CUy3u',
          defaultMessage: 'Economy Package',
          description: 'Form field label for economy package upgrade option',
        })
      }
      if (lowerCasedPackageType === 'vip') {
        return intl.formatMessage({
          id: 'ZuOxvNO_8WM0cEmKUxSl',
          defaultMessage: 'Business Package',
          description: 'Form field label for business package upgrade option',
        })
      }
      if (lowerCasedPackageType === 'platinum') {
        return intl.formatMessage({
          id: 'zgIkURUz394OONetU7qa',
          defaultMessage: 'First Class Package',
          description: 'Form field label for first class package upgrade option',
        })
      }
    } else if (offerIdWithoutPrefix === '6909c91b-cc92-4c0b-86d4-d663263436be') {
      // View package text
      if (lowerCasedPackageType === 'regular') {
        return intl.formatMessage({
          id: 'twkNLdfFFvZUaOHvwn2Y',
          defaultMessage: 'King or Twin Package',
          description: 'Label for king or twin room package option on the room page',
        })
      }
      if (lowerCasedPackageType === 'vip') {
        return intl.formatMessage({
          id: 'ejmqKjqopTHwUHGg6mcp',
          defaultMessage: 'Ocean View w. Club Access Upgrade',
          description: 'Label for ocean view with club access package upgrade',
        })
      }
      if (lowerCasedPackageType === 'platinum') {
        return intl.formatMessage({
          id: 'bMX_1_lXC4tiabNt3JrE',
          defaultMessage: 'Regency Suite w. Club Access Upgrade',
          description: 'Label for regency suite with club access package upgrade',
        })
      }
    } else if (offerIdWithoutPrefix === '1ea35824-8354-4656-9d76-c24d717ee700') {
      // Range view package text
      if (lowerCasedPackageType === 'regular') {
        return intl.formatMessage({
          id: '1pYVu_re4-taLU3fJxno',
          defaultMessage: 'Classic to King or Twin Package',
          description: 'Label for classic to king or twin room package option',
        })
      }
      if (lowerCasedPackageType === 'vip') {
        return intl.formatMessage({
          id: 'PBuQJYcQa_zkMj19Rz2K',
          defaultMessage: 'Superior to Ocean View w. Club Access Upgrade',
          description: 'Label for superior to ocean view with club access package upgrade',
        })
      }
      if (lowerCasedPackageType === 'platinum') {
        return intl.formatMessage({
          id: 'K75YURNAMghLubITvqoi',
          defaultMessage: 'Deluxe to Regency Suite w. Club Access Upgrade',
          description: 'Label for deluxe to regency suite with club access package upgrade',
        })
      }
    } else {
      // Standard package text
      if (lowerCasedPackageType === 'regular') {
        return intl.formatMessage({
          id: 'kqH53GfYaCU1bF8sk4HM',
          defaultMessage: 'Standard Package',
          description: 'Form field label for standard package option in the tours section',
        })
      }
      if (lowerCasedPackageType === 'vip') {
        return intl.formatMessage({
          id: '_sLmvnRw0iFsomO5DTVC',
          defaultMessage: 'Upgraded Package',
          description: 'Form field label for upgraded package option in the tours section',
        })
      }
      if (lowerCasedPackageType === 'platinum') {
        return intl.formatMessage({
          id: 'V6MqATsxRIjwdcPZQJ88',
          defaultMessage: 'Deluxe Package',
          description: 'Form field label for deluxe package option in the tours section',
        })
      }
    }

    // Fallback to the package type itself
    return candidatePackageType
  }, [intl, offer.id, variation.availablePackageUpgrades])

  const hasDiscount = useMemo(() => {
    return views.flatMap(view => view.options).some(option => extractTourV2PurchasableOptionDetails(option).hasDiscount)
  }, [views])

  const tourV2StartLocation = useMemo(() => [offer.variations[0]?.startLocation ?? ''], [offer])

  const isDepositAvailable = useMemo<boolean>(() => {
    if (!offer.paymentScheduleTemplate) return false
    const departureDate = moment(departure.startDate)
    const daysTillDeparture = departureDate.diff(moment(), 'days')
    return daysTillDeparture >= offer.paymentScheduleTemplate.minimumLeadDays
  }, [departure, offer])

  const handleTourDepositInfoClick = useCallback(async() => {
    if (offer.paymentScheduleTemplate) {
      const result = await showModal<TourDepositInfoModalResult>(<TourDepositInfoModal
        paymentScheduleTemplate={offer.paymentScheduleTemplate}
        primaryActionText={intl.formatMessage({
          id: 'CBMO8UWmHXxMFwSm7KG0',
          defaultMessage: 'Select departure',
          description: 'Button text to select departure in deposit info modal',
        })}
      />)
      if (result === 'primary-action') {
        onSelectClick()
      }
    }
  }, [offer.paymentScheduleTemplate, onSelectClick, showModal, intl])

  const pointsEarnCalculationOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      return [
        generateLoyaltyPointsCalculatorTourOptions(offer,
          {
            price: cheapestOption?.price,
            memberPrice: cheapestOption?.memberPrice,
            roomType: cheapestOption?.roomType,
            packageId: cheapestOption?.fkDepartureId,
            offerId: cheapestOption?.fkTourId,
            nights: departure.duration,
          }),
      ]
    },
    [cheapestOption, offer, departure.duration],
  )

  if (!cheapestOption || !details) {
    return <TourV2DepartureSoldOutTile
      departure={departure}
      offer={offer}
      travellers={travellers}
    />
  }

  return <Tile>
    <Group direction="vertical" gap={12}>
      <TourV2DepartureTileDates departure={departure} gridArea="dates" />
      <LoyaltyPointsEarnWithBreakdownModal calculationRequests={pointsEarnCalculationOptions} calculationType="estimate" />
      {props.isSpoofing && <CaptionTextBlock variant="medium" weight="bold" colour="urgency">
        {intl.formatMessage(
          {
            id: 'aizUTpnmkd0O0eZvicoj',
            defaultMessage: 'Days till Handback: {days}',
            description: 'Spoofing only: Days until handback date in the date selection or calendar picker',
          },
          { days: moment(departure.handbackDate).diff(moment(), 'days') },
        )}
      </CaptionTextBlock>}
    </Group>
    <TagGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      tabletGap={4}
      wrap="wrap"
    >
      {!isCruiseFlashOffer && <>
        {departure.isGuaranteed && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidCheckCircleIcon colour="highlight-secondary" />}
          endIcon={
            <Tooltip
              description={intl.formatMessage({
                id: 'HByBGoqmDzI9CAWgM7GP',
                defaultMessage: 'Guaranteed with 2 passengers',
                description: 'Tooltip description for guaranteed departure shown on hover',
              })}
            >
              <LineInfoCircleIcon colour="neutral-two" />
            </Tooltip>
          }
        >
          {intl.formatMessage({
            id: '7aANBcmxmvCSeVF3Fk_I',
            defaultMessage: 'Guaranteed departure',
            description: 'Form field label for guaranteed departure in the tours section',
          })}
        </CaptionTextBlock>}
        {cheapestOption.inventoryLeft <= 4 && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidExclamationCircleIcon colour="urgency" />}
        >
          {intl.formatMessage(
            {
              id: 'WDXbPdVZBj6bP4KAJqXw',
              defaultMessage: '{count, plural, one {# spot left} other {# spots left}}',
              description: 'Inventory remaining count for departure in the tours section',
            },
            { count: cheapestOption.inventoryLeft },
          )}
        </CaptionTextBlock>}
        {(hasPackageUpgrade || hasRoomUpgrade) && <CaptionTextBlock
          variant="medium"
          weight="bold"
          startIcon={<SolidCheckCircleIcon colour="highlight-secondary" />}
        >
          {intl.formatMessage({
            id: '_67_Mo0PIWdhYs-x1407',
            defaultMessage: 'Upgrades available',
            description: 'Label indicating package or room upgrades are available',
          })}
        </CaptionTextBlock>}
        {cheapestOption.inventoryLeft > 4 && props.isSpoofing && <CaptionTextBlock variant="medium" weight="bold" colour="urgency">
          {intl.formatMessage(
            {
              id: 'PUrKmm7wp9R5fJXOj9yT',
              defaultMessage: '{count} {roomType} spots left',
              description: 'Spoofing only: Inventory count with room type in the tours section',
            },
            { count: cheapestOption.inventoryLeft, roomType: cheapestOption.roomType },
          )}
        </CaptionTextBlock>}
      </>}
    </TagGridArea>
    <MemberGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      desktopGap={4}
      wrap="wrap"
    >
      {isDepositAvailable && <TourDepositTag offer={offer} onInfoClick={handleTourDepositInfoClick} />}
      {hasDiscount && <TourDiscountLabel />}
      {!!cheapestPackageTypeForTile && (
        <PackageLabel kind={isCheapestPackageTypeSelected ? 'primary' : 'tertiary'} variant={isCheapestPackageTypeSelected ? 'highlight-secondary' : 'ghost'}>
          {getPackageTagLabel(cheapestPackageTypeForTile)}
        </PackageLabel>
      )}
      {showPrivateRequest && <TextLink size="medium" weight="regular" onClick={onRequestPrivateDeparture}>
        {intl.formatMessage({
          id: 'wJ6TsQI9MqBJPI04lV3m',
          defaultMessage: 'Request a private departure',
          description: 'Action button label to request a private tour departure',
        })}
      </TextLink>}
    </MemberGridArea>
    <Group
      style={{ gridArea: 'prices' }}
      direction="horizontal"
      gap={12}
      verticalAlign="end"
      horizontalAlign="space-between"
    >
      <VerticalSpacer gap={4}>
        <div>
          <PriceRowPriceCaption>
            {intl.formatMessage({
              id: 'eYSFqXVFsgoMsnbiqQuW',
              defaultMessage: 'From',
              description: 'Price caption prefix text indicating the starting price for an offer or package',
            })}
          </PriceRowPriceCaption>
          {!pricePerDayDetails && <LuxPlusPriceStack
            size="L"
            pricing={details.pricing}
            memberPricing={details.memberPricing}
            isTour
          />}
          {pricePerDayDetails && <LuxPlusPriceStack
            size="L"
            pricing={pricePerDayDetails.pricing}
            memberPricing={pricePerDayDetails.memberPricing}
            isTour
          />}
          {details.hasDiscount && !(canViewLuxPlusBenefits && details.memberPrice) && <PriceRowSale value={details.fullPricePerPerson} />}
          <PriceRowAgentHubCommission
            kind="plain"
            offerTotal={total}
            offerLocations={tourV2StartLocation}
            offerId={offer.id}
            productType={offer.productType}
            optionId={departure.id}
          />
          {!!details.valuedAt && <>
            {!pricePerDayDetails?.valueAt && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={details.valuedAt.price}
              discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
            />}
            {!!pricePerDayDetails?.valueAt && <PriceRowValueDiscountWithCaption
              size="M"
              originalValue={pricePerDayDetails?.valueAt}
              discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
            />}
          </>}
          <PriceRowCaption>
            {travellers.length <= 1 && details.roomTypeName}
            {travellers.length > 1 && <FormattedMessage
              id="wBeCp44hqVkQssRdmbPh"
              defaultMessage="{count, plural, one {# room} other {# rooms}} · {price} total"
              description="Room count with total price for tour departure tile"
              values={{ count: travellers.length, price: <FormatCurrency value={total} /> }}
            />}
          </PriceRowCaption>
        </div>
        {travellers.length > 1 && <TourV2DeparturePricingDropdown
          travellers={travellers}
          departure={departure}
          offer={offer}
          variation={variation}
        /> }
      </VerticalSpacer>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" gap={8}>
          <TextButton kind="primary" onClick={onSelectClick}>
            {intl.formatMessage({
              id: 'YJfvoTAzXeAccoD14t4f',
              defaultMessage: 'Select',
              description: 'Action button label to select departure in the tours section',
            })}
          </TextButton>
        </Group>
      </CSSBreakpoint>
    </Group>
    <CSSBreakpoint min="tablet" style={{ gridArea: 'cta' }}>
      <Group direction="vertical" gap={8}>
        <TextButton kind="primary" onClick={onSelectClick} data-testid="departure-book-button">
          {intl.formatMessage({
            id: 'YJfvoTAzXeAccoD14t4f',
            defaultMessage: 'Select',
            description: 'Action button label to select departure in the tours section',
          })}
        </TextButton>
      </Group>
    </CSSBreakpoint>
  </Tile>
}
export default connect<MappedStateProps, {}, {}, App.State>((state) => {
  return {
    isSpoofing: state.auth.account.isSpoofed,
  }
})(memo(TourV2DepartureTile))
